import React from 'react';
import { useTranslation } from 'react-i18next';
import { createProductImageUrl } from '../../utils/productImageUrl';
import { formatPurchaseDate, isPurchaseDateOnOrAfter } from '../../utils/formatPurchaseDate';
import { formatPrice } from '../../utils/formatPrice';

const INVOICE_BASE_URL = 'https://clientsig.oss-cn-hongkong.aliyuncs.com/pos_invoice';

function linePrice(p) {
  const unit = Number(p.product_price) - Number(p.product_discount || 0);
  const qty = Number(p.product_qty) || 0;
  return unit * qty;
}

export default function OfflineOrderDetail({ groupedOrder, onBack }) {
  const { t, i18n } = useTranslation();
  const lines = groupedOrder.product_list || [];
  const qtySum = lines.reduce((s, p) => s + (Number(p.product_qty) || 0), 0);
  const subtotalComputed = lines.reduce((s, p) => s + linePrice(p), 0);
  const currency = groupedOrder.product_price_currency || (lines[0] && lines[0].product_price_currency) || '';

  const storeName = groupedOrder.shop_name || groupedOrder.purchase_place || t('ordersLayout.storeAddressPending');
  const showDownloadInvoice = isPurchaseDateOnOrAfter(groupedOrder.purchase_date, '2026-04-30');

  const openInvoice = () => {
    const orderId = groupedOrder.order_id;
    if (!orderId) return;
    window.open(`${INVOICE_BASE_URL}/${orderId}-e.pdf`, '_blank', 'noopener,noreferrer');
  };

  return (
    <main className="orders-offline-detail-main">
      <section className="page-title" aria-labelledby="order-title">
        <button type="button" className="back-link" onClick={onBack} id="order-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          {t('ordersLayout.orderNumberHeading', { id: groupedOrder.order_id })}
        </button>
        <div className="confirmed">
          {t('ordersLayout.statusConfirmed')} {formatPurchaseDate(groupedOrder.purchase_date, i18n.language)}
        </div>
      </section>

      <section className="order-layout" aria-label="Order details">
        <aside className="info-card" aria-label="Purchase and store information">
          <div className="info-section">
            <div className="eyebrow">{t('ordersLayout.purchaseDateLabel')}</div>
            <div className="body-copy">{formatPurchaseDate(groupedOrder.purchase_date, i18n.language)}</div>
          </div>

          <div className="info-section">
            <div className="eyebrow">{t('ordersLayout.storeLabel')}</div>
            <div className="body-copy">{storeName}</div>
            <div className="body-copy">{groupedOrder.thk_address}</div>
          </div>

          {showDownloadInvoice && (
            <button className="invoice-button" type="button" onClick={openInvoice}>
              {t('ordersLayout.downloadInvoice')}
              <img src="/download-icon.svg" width="18" height="18" alt="" aria-hidden="true" />
            </button>
          )}
        </aside>

        <article className="summary-card" aria-label="Order summary">
          {lines.map((p) => {
            const img = p.picture_url || createProductImageUrl(p);
            const name = p.product_name || t('ordersLayout.productNameFallback');
            const warranty = p.warranty_date;
            return (
              <div key={p.id || p.product_sku} className="line-item">
                <span className="quantity">{p.product_qty}</span>
                <div className="thumb" aria-hidden="true">
                  {img ? <img src={img} alt="" /> : null}
                </div>
                <div>
                  <h2 className="item-name">{name}</h2>
                  <div className="item-meta">
                    {t('ordersLayout.skuPrefix')}: {p.product_sku}
                    {p.product_size && p.product_size !== 'NO-SIZE' && (
                      <span>
                        <br />
                        {t('ordersLayout.sizeLabel')}: {p.product_size}
                      </span>
                    )}
                    {warranty && (
                      <span>
                        <br />
                        {warranty === 'OUT OF WARRANTY' ? '' : t('ordersLayout.warrantyExpires')}{' '}
                        {formatPurchaseDate(warranty, i18n.language)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="price">
                  {formatPrice(linePrice(p), p.product_price_currency || currency, i18n.language)}
                </div>
              </div>
            );
          })}

          <div className="totals" aria-label="Order totals">
            <div className="total-row">
              <span>
                {t('ordersLayout.subtotalLabel')} – {t('ordersLayout.itemsCount', { count: qtySum })}
              </span>
              <span>
                {formatPrice(subtotalComputed, currency, i18n.language)}
              </span>
            </div>
            <div className="total-row">
              <b>{t('ordersLayout.totalLabel')}</b>
              <strong>
                {formatPrice(groupedOrder.order_grand_total, currency, i18n.language)}
              </strong>
            </div>
          </div>
        </article>
      </section>
    </main>
  );
}



// WEBPACK FOOTER //
// ./src/js/components/ordersLayout/OfflineOrderDetail.jsx